import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { Text, InlineStack, Badge, FormLayout, TextField, Select, Button, Form, InlineGrid, Box, Card, BlockStack, ButtonGroup, Tooltip, useBreakpoints, Divider, Layout, Toast } from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';
import { useDispatch, useSelector } from 'react-redux';
import { getGoldPurityPriceAsync, createPriceUpdateAsync } from '../../redux/priceUpdate/index';
import { getAllPriceUpdate } from '../../redux/priceUpdate/slice';
import Toggle from '../common/Toggle';
import SaveBarComponent from '../common/SaveBar';
import AppBridgeSavebar from '../common/AppBridgeSavebar';
import { calculateUpdateDates, extractDate, INTERVAL_OPTIONS, isAdmin, NUMBER_OF_INTERVAL_OPTIONS } from '../../utils';
import Placeholder from '../common/Placeholder';
import { manualProcessRulesAsync } from '../../redux/rule';
import { getAllRule } from '../../redux/rule/slice';

const PriceUpdate = ({ priceUpdateData, handleCallBack }) => {
    const dispatch = useDispatch();
    const { smUp } = useBreakpoints();
    const { goldPurityPriceData, isRefreshing, isSaving } = useSelector(getAllPriceUpdate);
    const { isManualProcessRulesLoading } = useSelector(getAllRule);
    const { store_data } = useSelector((store) => store.commonData);
    const [selected, setSelected] = useState(priceUpdateData?.updateMethod === 'Auto');
    const [isVAT, setIsVAT] = useState(false);
    const [intervalUnit, setIntervalUnit] = useState(priceUpdateData?.auto?.interval?.unit);
    const [intervalValue, setIntervalValue] = useState(priceUpdateData?.auto?.interval?.value);
    const [lastUpdateDate, setLastUpdateDate] = useState('');
    const [nextUpdateDate, setNextUpdateDate] = useState('');
    const [saveBarOpen, setSaveBarOpen] = useState(false);
    const [tax_charge, setTaxCharge] = useState(0);
    const [shipping_charge, setShippingCharge] = useState(0);
    const [rates, setRates] = useState({
        '10k': '',
        '14k': '',
        '16k': '',
        '18k': '',
        '20k': '',
        '22k': '',
        '24k': '',
        'XAG': '',
        'XPT': '',
        'XPD': ''
    });
    const [priceSource, setPriceSource] = useState(priceUpdateData?.priceSource || 'api');
    const [message, setMessage] = useState('');
    const [active, setActive] = useState(false);

    const currency = store_data?.shop_data?.currency || 'USD';

    useEffect(() => {
        setSelected(priceUpdateData?.updateMethod === 'Auto');
        setIntervalUnit(priceUpdateData?.auto?.interval?.unit);
        setIntervalValue(priceUpdateData?.auto?.interval?.value);
        setLastUpdateDate(extractDate(priceUpdateData?.auto?.lastUpdate));
        setNextUpdateDate(extractDate(priceUpdateData?.auto?.nextUpdate));
        setTaxCharge(priceUpdateData?.tax_charge);
        setShippingCharge(priceUpdateData?.shipping_charge);
        setPriceSource(priceUpdateData?.priceSource || 'api');
        setIsVAT(priceUpdateData?.isVAT);
        if (priceUpdateData?.manual?.rates) {
            setRates(priceUpdateData?.manual?.rates);
        }
    }, [priceUpdateData]);

    useEffect(() => {
        if (goldPurityPriceData) {
            setRates(goldPurityPriceData);
        }
    }, [goldPurityPriceData]);

    const toggleActive = useCallback((message) => {
        setMessage(message);
        setActive((active) => !active);
    }, []);

    const toastMarkup = active ? (
        <Toast duration={2000} content={message} onDismiss={() => toggleActive('')} />
    ) : null;

    const handleRateChange = ({ name, value }) => {
        setRates((prevRates) => ({
            ...prevRates,
            [name]: value,
        }));
        handleSaveBarOpen();
    };

    const handleIntervalUnitChange = (value) => {
        setIntervalUnit(value);
        handleSaveBarOpen();
    };

    const handleRefresh = () => {
        dispatch(getGoldPurityPriceAsync());
    };

    const handleSave = () => {
        const currentDate = new Date();
        const isIntervalChanged = intervalUnit !== priceUpdateData?.auto?.interval?.unit || intervalValue !== priceUpdateData?.auto?.interval?.value;
        const updateDates = selected ? calculateUpdateDates(currentDate, intervalValue, intervalUnit) : { lastUpdate: priceUpdateData?.auto?.nextUpdate, nextUpdate: currentDate.toISOString() }
        const payload = {
            updateMethod: selected ? "Auto" : "Manual",
            auto: {
                interval: {
                    value: intervalValue,
                    unit: intervalUnit,
                },
                lastUpdate: isIntervalChanged ? updateDates.lastUpdate : priceUpdateData?.auto?.lastUpdate,
                nextUpdate: isIntervalChanged ? updateDates.nextUpdate : priceUpdateData?.auto?.nextUpdate,
            },
            manual: {
                rates
            },
            isVAT,
            tax_charge,
            shipping_charge,
            priceSource
        };
        dispatch(createPriceUpdateAsync({
            callback: handleDiscard,
            payload
        }));
    };

    const handleDiscard = () => {
        setSaveBarOpen(false);
        handleCallBack();
    };

    const handleSaveBarOpen = () => {
        setSaveBarOpen(true);
    };

    const handleManualProcessRule = () => {
        dispatch(manualProcessRulesAsync({
            callback: (success, message) => {
                if (success) {
                    toggleActive(message);
                }
            }
        }))
    };

    return (
        <Layout.Section>
            <InlineGrid columns={{ xs: "1fr", md: "2fr 5fr" }} gap="400">
                <Box
                    as="section"
                    paddingInlineStart={{ xs: 400, sm: 0 }}
                    paddingInlineEnd={{ xs: 400, sm: 0 }}
                >
                    <BlockStack gap="400">
                        <Text as="h3" variant="headingMd">
                            Price Update Mode
                        </Text>
                        <Text as="p" variant="bodyMd">
                            Enable or disable automatic updates for live gold prices by choosing your preferred method
                        </Text>
                    </BlockStack>
                </Box>
                <Card roundedAbove="sm">
                    <BlockStack gap="400">
                        <InlineStack align="space-between" blockAlign="center">
                            <InlineStack align="start" gap="200" blockAlign="center">
                                <Text variant="headingSm" as="span">
                                    Enable Auto Price Update
                                </Text>
                                <Badge tone={selected ? 'success' : 'attention'}>
                                    {selected ? 'Enabled' : 'Disabled'}
                                </Badge>
                                &nbsp;&nbsp;&nbsp;
                                <Toggle
                                    selected={selected}
                                    ariaLabel="Toggle auto price update"
                                    onClick={() => {
                                        setSelected((prev) => !prev);
                                        handleSaveBarOpen();
                                    }}
                                />
                                <Text as="p" variant="bodyMd">
                                    When enabled Live gold prices will automatically update at your configured interval, keeping your product prices accurate without manual effort.
                                </Text>
                            </InlineStack>
                        </InlineStack>
                        <Select
                            label="Price Source"
                            options={[
                                { label: "Live Gold API", value: "api" },
                                { label: "Variant Metafields", value: "metafield" },
                            ]}
                            value={priceSource}
                            onChange={(value) => {
                                setPriceSource(value);
                                handleSaveBarOpen();
                            }}
                        />
                        <InlineStack align="space-between" gap="200" blockAlign="start">
                            <Box>
                                <Text variant="headingSm" as="span">
                                    Update Variant Prices
                                </Text>
                                <Text as="p" variant="bodyMd">
                                    Access the variant prices from the {priceSource === "api" ? "live gold API" : "variant metafields"}.
                                </Text>
                            </Box>
                            <Tooltip content={`Update prices based on ${priceSource === "api" ? "Live Gold API" : "Metafields"}`}>
                                {!selected &&
                                    <Button loading={isManualProcessRulesLoading} onClick={handleManualProcessRule} variant='primary'>
                                        Update Variant Prices
                                    </Button>
                                }
                            </Tooltip>
                        </InlineStack>
                        <FormLayout.Group>
                            <TextField
                                label="Last updated"
                                type="text"
                                value={lastUpdateDate}
                                disabled
                            />
                            <TextField
                                label="Next updated"
                                type="text"
                                value={nextUpdateDate}
                                disabled
                            />
                        </FormLayout.Group>
                        <InlineStack align="space-between" blockAlign="center">
                            <InlineStack align="start" gap="200" blockAlign="center">
                                <Text variant="headingSm" as="span">
                                    Enable VAT (Value Added Tax)
                                </Text>
                                <Badge tone={isVAT ? 'success' : 'attention'}>
                                    {isVAT ? 'Enabled' : 'Disabled'}
                                </Badge>
                                &nbsp;&nbsp;&nbsp;
                                <Toggle
                                    selected={isVAT}
                                    ariaLabel="Toggle auto VAT price update"
                                    onClick={() => {
                                        setIsVAT((prev) => !prev);
                                        handleSaveBarOpen();
                                    }}
                                />
                                 <Text as="p" variant="bodyMd">
                                    {isVAT ? "VAT is enabled. We use the VAT metafield for % of final price. Default is 5%. If a custom VAT metafield is set, we use that value instead." : "VAT is disabled."}
                                </Text>
                            </InlineStack>
                        </InlineStack>
                    </BlockStack>
                </Card>
            </InlineGrid>
            <Placeholder />
            {smUp ? <Divider /> : null}
            <Placeholder />
            <InlineGrid columns={{ xs: "1fr", md: "2fr 5fr" }} gap="400">
                <Box
                    as="section"
                    paddingInlineStart={{ xs: 400, sm: 0 }}
                    paddingInlineEnd={{ xs: 400, sm: 0 }}
                >
                    <BlockStack gap="400">
                        <Text as="h3" variant="headingMd">
                            Price Update Settings
                        </Text>
                        <Text as="p" variant="bodyMd">
                            Configure your price update preferences.
                        </Text>
                    </BlockStack>
                </Box>
                <Card roundedAbove="sm">
                    <BlockStack gap="400">
                        <InlineStack align='space-between' blockAlign='center'>
                            <Text variant="headingSm" as="span">
                                Set the number of days for auto price updates
                            </Text>
                        </InlineStack>
                        <Fragment>
                            <FormLayout.Group>
                                <InlineGrid gap="200" columns={2}>
                                    <Select
                                        label="Select Interval Unit"
                                        options={INTERVAL_OPTIONS}
                                        value={intervalUnit}
                                        onChange={handleIntervalUnitChange}
                                    />
                                    <TextField
                                        label="Price Update Interval (Days)"
                                        type="number"
                                        value={intervalValue}
                                        min={NUMBER_OF_INTERVAL_OPTIONS[intervalUnit]?.min}
                                        max={NUMBER_OF_INTERVAL_OPTIONS[intervalUnit]?.max}
                                        onChange={(value) => {
                                            setIntervalValue(value);
                                            handleSaveBarOpen();
                                        }}
                                        suffix={intervalUnit}
                                        error={intervalValue > NUMBER_OF_INTERVAL_OPTIONS[intervalUnit]?.max ? 'Interval value cannot exceed the maximum value' : null}
                                    />
                                </InlineGrid>
                            </FormLayout.Group>
                        </Fragment>
                    </BlockStack>
                    <Placeholder />
                    <BlockStack gap="400">
                        <InlineStack align='space-between' blockAlign='center'>
                            <Text variant="headingSm" as="span">
                                Enter the daily gold rates manually
                            </Text>
                            <InlineStack align='end'>
                                <ButtonGroup gap='loose'>
                                    <Tooltip content='Refresh live gold price'>
                                        <Button icon={RefreshIcon} onClick={handleRefresh} loading={isRefreshing} variant='tertiary'>
                                        </Button>
                                    </Tooltip>
                                </ButtonGroup>
                            </InlineStack>
                        </InlineStack>
                        <div className='manual-price-update'>
                            <Form>
                                <FormLayout.Group>
                                    <InlineGrid gap="400" columns={3}>
                                        {Object.keys(rates).slice(0, 7).map((key) => (
                                            <TextField
                                                key={key}
                                                label={`Gold Price ${key} / Gram`}
                                                type="number"
                                                min="0"
                                                value={rates[key]}
                                                onChange={(value) => handleRateChange({ name: key, value })}
                                                name={key}
                                                suffix={currency}
                                                requiredIndicator
                                            />
                                        ))}
                                        {Object.keys(rates).slice(7, 10).map((key) => (
                                            <TextField
                                                key={key}
                                                label={`${key === 'XAG' ? 'Silver XAG' : key === 'XPT' ? 'Platinum XPT' : 'Palladium XPD'} Price`}
                                                type="number"
                                                min="0"
                                                value={rates[key]}
                                                onChange={(value) => handleRateChange({ name: key, value })}
                                                name={key}
                                                suffix={currency}
                                                requiredIndicator
                                            />
                                        ))}
                                    </InlineGrid>
                                </FormLayout.Group>
                            </Form>
                        </div>
                    </BlockStack>
                </Card>
            </InlineGrid>
            <Placeholder />
            {false && <InlineGrid columns={{ xs: "1fr", md: "2fr 5fr" }} gap="400">
                <Box
                    as="section"
                    paddingInlineStart={{ xs: 400, sm: 0 }}
                    paddingInlineEnd={{ xs: 400, sm: 0 }}
                >
                    <BlockStack gap="400">
                        <Text as="h3" variant="headingMd">
                            Tax & Shipping Settings
                        </Text>
                        <Text as="p" variant="bodyMd">
                            Set the applicable tax percentage and shipping charges for your products.
                        </Text>
                    </BlockStack>
                </Box>
                <Card>
                    <BlockStack gap="400" padding="400">
                        <FormLayout.Group condensed>
                            <TextField
                                label="Tax (%)"
                                type="text"
                                placeholder="Enter you tax charges"
                                suffix='%'
                                autoComplete="off"
                                value={tax_charge}
                                onChange={(val) => {
                                    handleSaveBarOpen();
                                    setTaxCharge(val);
                                }}
                            />
                            <TextField
                                label="Shipping Charge"
                                type="text"
                                suffix={currency}
                                placeholder="Enter your shipping charges"
                                autoComplete="off"
                                value={shipping_charge}
                                onChange={(val) => {
                                    handleSaveBarOpen();
                                    setShippingCharge(val);
                                }}
                            />
                        </FormLayout.Group>
                    </BlockStack>
                </Card>
            </InlineGrid>}
            {saveBarOpen && !isAdmin &&
                <SaveBarComponent
                    saveAction={handleSave}
                    discardAction={handleDiscard}
                    saveLoading={isSaving}
                    discardLoading={false}
                    message={"Unsaved changes"}
                    disabled={intervalValue > NUMBER_OF_INTERVAL_OPTIONS[intervalUnit].max}
                />
            }
            {saveBarOpen && isAdmin &&
                <AppBridgeSavebar
                    visible={saveBarOpen}
                    saveAction={{ disabled: intervalValue > NUMBER_OF_INTERVAL_OPTIONS[intervalUnit].max, loading: isSaving, onAction: handleSave }}
                    discardAction={{ onAction: handleDiscard }}
                />
            }
            {toastMarkup}
        </Layout.Section>
    );
};

export default PriceUpdate;
